import React, { useCallback, useEffect, useState } from 'react';

import Table, { IColumn } from '~/components/Table';
import api from '~/services/api';
import ExportCsv from '../ExportCsv';
import Loading from '../Loading';

import { ModCatTable } from './styles';
import ExportXls from '../ExportXlsx';

interface IModCatResponse {
  modality_id: number;
  category_id: number;
  event_id: number;
  event_name: string;
  modality_name: string;
  category_name: string;
  quantity: number;
  qty_total: number;
  qty_sold: string;
  qty_courtesy: string;
}

interface IPagedResponse {
  data: IModCatResponse[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IModCatRow {
  id: number;
  event: string;
  modality: string;
  category: string;
  total: number;
  paid: number;
  courtesy: number;
}

interface IDashboardModCatTableProps {
  selectedEventId?: number | null;
}

const columns: IColumn[] = [
  {
    name: 'Evento',
    selector: 'event',
    sortable: false,
    cell: (row) => <span title={row.event}>{row.event}</span>,
  },
  {
    name: 'Modalidade',
    selector: 'modality',
    sortable: false,
    cell: (row) => <span title={row.modality}>{row.modality}</span>,
  },
  {
    name: 'Categoria',
    selector: 'category',
    sortable: false,
    cell: (row) => <span title={row.category}>{row.category}</span>,
  },
  {
    name: 'Total',
    selector: 'total',
    sortable: false,
  },
  {
    name: 'Pago',
    selector: 'paid',
    sortable: false,
  },
  {
    name: 'Cortesia',
    selector: 'courtesy',
    sortable: false,
  },
];

const DashboardModCatTable: React.FC<IDashboardModCatTableProps> = ({
  selectedEventId,
}) => {
  const [data, setData] = useState<IModCatRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const handleLoad = useCallback(
    async (page: number, eventId?: number | null) => {
      const response = await api.get<IPagedResponse>(
        '/sales/organizers/modality-category',
        {
          params: { page, event_id: eventId },
        }
      );

      const mapped = response.data.data.map<IModCatRow>((item, index) => ({
        id: index,
        event: item.event_name,
        modality: item.modality_name,
        category: item.category_name,
        total: item.qty_total,
        paid: parseInt(item.qty_sold, 10) || 0,
        courtesy: parseInt(item.qty_courtesy, 10) || 0,
      }));

      setData(mapped);
      setTotal(response.data.total);
      setTotalPages(response.data.pages);
    },
    []
  );

  useEffect(() => {
    setSelectedPage(1);
    if (selectedEventId) {
      handleLoad(1, selectedEventId);
    } else {
      setData([]);
      setTotal(0);
      setTotalPages(0);
    }
  }, [handleLoad, selectedEventId]);

  const handleChangePage = useCallback(
    (page: number) => {
      if (page <= totalPages) {
        setSelectedPage(page);
        handleLoad(page, selectedEventId);
      }
    },
    [handleLoad, selectedEventId, totalPages]
  );

  const handleClickExportCsv = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/sales/organizers/modality-category', {
        params: { event_id: selectedEventId },
      });

      const mapped = (response.data ?? []).map((item: any) => ({
        event: item.event_name,
        modality: item.modality_name,
        category: item.category_name,
        total: parseInt(item.qty_total, 10) || 0,
        paid: parseInt(item.qty_sold, 10) || 0,
        courtesy: parseInt(item.qty_courtesy, 10) || 0,
      }));
      return mapped;
    } catch (err) {
      return [];
    } finally {
      setLoading(false);
    }
  }, [selectedEventId]);

  return (
    <ModCatTable className="col-12 px-0 mt-3">
      <div className="zoom bg-white p-3">
        <div className="d-flex align-items-center justify-content-between">
          <h2 className="h4 fw-semibold mb-0">
            Inscrições por Modalidade/Categoria
          </h2>
          <div>
            <ExportCsv
              data={data}
              columns={columns}
              fileName="Inscricoes_modalidade_categoria"
              onClick={handleClickExportCsv}
            />
            <ExportXls
              data={data}
              columns={columns}
              fileName="Inscricoes_modalidade_categoria"
              onClick={handleClickExportCsv}
              className="export-xls"
            />
          </div>
        </div>
        <hr />
        <div className="">
          <Table
            className="table p-0 mb-0"
            columns={columns}
            data={data}
            totalData={total}
            pagination
            selectedPage={selectedPage}
            onChangePage={handleChangePage}
          />
        </div>
      </div>
      <Loading active={loading} text="Carregando..." />
    </ModCatTable>
  );
};

export default DashboardModCatTable;
